import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Bell, ChevronDown, Headphones, BookOpen, PencilLine, Mic,
  LogOut, User, GraduationCap, Settings, Wallet, Users, ShoppingBag, Video, Trophy, Bot
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPerm } from "@/lib/permissions";

// Academic / General context (persisted)
export function useExamMode() {
  const [mode, setMode] = useState(() => localStorage.getItem("lingoq_exam_mode") || "ACADEMIC");
  useEffect(() => { localStorage.setItem("lingoq_exam_mode", mode); }, [mode]);
  return [mode, setMode];
}

const practiceMenu = [
  {
    title: "LISTENING", icon: Headphones,
    items: [
      { label: "Part 1", to: "/app/tests?section=LISTENING&part=1" },
      { label: "Part 2", to: "/app/tests?section=LISTENING&part=2" },
      { label: "Part 3", to: "/app/tests?section=LISTENING&part=3" },
      { label: "Part 4", to: "/app/tests?section=LISTENING&part=4" },
    ],
  },
  {
    title: "READING", icon: BookOpen,
    items: [
      { label: "Academic",  to: "/app/tests?section=READING&kind=ACADEMIC" },
      { label: "General",   to: "/app/tests?section=READING&kind=GENERAL"  },
    ],
  },
  {
    title: "WRITING", icon: PencilLine,
    items: [
      { label: "Task 1: Diagram (Academic)",     to: "/app/writing?task=TASK1" },
      { label: "Task 2: Essay (General / Academic)", to: "/app/writing?task=TASK2" },
    ],
  },
  {
    title: "SPEAKING", icon: Mic,
    items: [
      { label: "🎬 Virtual Examiner Room (3D)", to: "/app/speaking-room" },
      { label: "Part 1: Question & Answers",   to: "/app/speaking?part=1" },
      { label: "Part 2: Cue Cards",            to: "/app/speaking?part=2" },
      { label: "Part 3: Q&A (Cue Cards)",      to: "/app/speaking?part=3" },
    ],
  },
  {
    title: "OTHER EXAMS", icon: BookOpen,
    items: [
      { label: "PTE Academic",         to: "/app/pte" },
      { label: "Duolingo English Test", to: "/app/duolingo" },
      { label: "Oxford ELLT",          to: "/app/oxford" },
    ],
  },
];

export default function DashboardLayout({ children, title, hideHeader = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [examMode, setExamMode] = useExamMode();
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const practiceRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (practiceRef.current && !practiceRef.current.contains(e.target)) setPracticeOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#F6F8FB] text-slate-900">
      {/* TOP NAVIGATION */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="w-full px-8 h-[72px] flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/app" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-sm border-2 border-[#F77F00] flex items-center justify-center text-[#F77F00] font-display font-bold text-lg"
              style={{ boxShadow: "0 0 12px rgba(247,127,0,0.40)" }}>
              E
            </div>
            <div className="font-display text-2xl tracking-tight leading-none">
              <div>
                <span className="text-[#003049] font-bold">EnglishTest</span>
                <span className="text-[#F77F00] font-bold">.UK</span>
              </div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-slate-500 mt-0.5">// PASS · NOT JUST PRACTICE</div>
            </div>
          </Link>

          {/* Center nav */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/app" end data-testid="nav-home" className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-full ${isActive ? "text-[#F77F00]" : "text-slate-700 hover:text-[#003049]"}`}>
              Home
            </NavLink>
            <div className="relative" ref={practiceRef}>
              <button onClick={() => setPracticeOpen((v) => !v)} data-testid="nav-practice"
                className={`px-4 py-2 text-sm font-medium rounded-full inline-flex items-center gap-1 ${practiceOpen ? "text-[#F77F00]" : "text-slate-700 hover:text-[#003049]"}`}>
                Practice <ChevronDown className={`w-3.5 h-3.5 transition-transform ${practiceOpen ? "rotate-180" : ""}`} />
              </button>
              {practiceOpen && <PracticeMegaMenu onClose={() => setPracticeOpen(false)} />}
            </div>
            <NavLink to="/app/mock" data-testid="nav-mock" className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-full ${isActive ? "text-[#F77F00]" : "text-slate-700 hover:text-[#003049]"}`}>
              Mock Test
            </NavLink>
            <NavLink to="/app/strategy" data-testid="nav-strategy" className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-full ${isActive ? "text-[#F77F00]" : "text-slate-700 hover:text-[#003049]"}`}>
              Strategy Video
            </NavLink>
            <NavLink to="/pricing" data-testid="nav-buy" className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-full ${isActive ? "text-[#F77F00]" : "text-slate-700 hover:text-[#003049]"}`}>
              Buy
            </NavLink>
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3">
            {/* Academic/General toggle */}
            <div className="hidden sm:flex p-1 rounded-full bg-[#FFE4E6] text-xs font-semibold" data-testid="exam-mode-toggle">
              <button onClick={() => setExamMode("ACADEMIC")} data-testid="mode-academic"
                className={`px-3.5 py-1.5 rounded-full transition-all ${examMode === "ACADEMIC" ? "bg-[#F77F00] text-white shadow-sm" : "text-[#9F1239]"}`}>
                Academic
              </button>
              <button onClick={() => setExamMode("GENERAL")} data-testid="mode-general"
                className={`px-3.5 py-1.5 rounded-full transition-all ${examMode === "GENERAL" ? "bg-[#F77F00] text-white shadow-sm" : "text-[#9F1239]"}`}>
                General
              </button>
            </div>
            {/* Notifications */}
            <button className="p-2 rounded-full hover:bg-slate-100 relative" data-testid="topbar-notif">
              <Bell className="w-5 h-5 text-slate-600" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#F77F00]" />
            </button>
            {/* User dropdown */}
            <div className="relative" ref={userRef}>
              <button onClick={() => setUserOpen((v) => !v)} data-testid="topbar-user"
                className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-full hover:bg-slate-100">
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-slate-500" />
                </div>
                <div className="hidden md:block text-left leading-tight">
                  <div className="text-[11px] text-[#F77F00] font-semibold uppercase">
                    {user?.subscription_status === "active" ? "Pro" : "Free"}
                  </div>
                  <div className="text-xs text-slate-700 truncate max-w-[120px]">
                    {user?.name || `User #${(user?.id || "").slice(0, 8)}`}
                  </div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
              {userOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-2xl shadow-[0_20px_50px_-20px_rgba(11,45,92,0.20)] py-2 z-50">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    <div className="text-[11px] text-[#F77F00] mt-1 font-mono">{user?.role}</div>
                  </div>
                  <MenuLink to="/app/profile" icon={User} label="Profile" />
                  <MenuLink to="/app/leaderboard" icon={Trophy} label="Leaderboard" />
                  <MenuLink to="/app/classrooms" icon={Video} label="Live Classes" />
                  <MenuLink to="/app/coach" icon={Bot} label="AI Coach" />
                  {hasPerm(user, "assignment:create") && <MenuLink to="/app/teacher" icon={GraduationCap} label="Teacher" />}
                  {hasPerm(user, "trial:view") && <MenuLink to="/app/admin" icon={Settings} label="Admin" />}
                  {hasPerm(user, "user:view") && <MenuLink to="/app/admin/users" icon={Users} label="Users" />}
                  {hasPerm(user, "finance:reports") && <MenuLink to="/app/finance" icon={Wallet} label="Finance" />}
                  <Link to="/pricing" className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <ShoppingBag className="w-4 h-4" /> Upgrade plan
                  </Link>
                  <button onClick={handleLogout} data-testid="logout-btn" className="w-full text-left flex items-center gap-2.5 px-4 py-2 text-sm text-[#F77F00] hover:bg-[#FFF4E5] border-t border-slate-100 mt-1">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-8 py-8">
        {!hideHeader && title && (
          <h1 className="font-display text-2xl text-[#003049] mb-6" data-testid="page-title">{title}</h1>
        )}
        {children}
      </main>
    </div>
  );
}

function MenuLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
      <Icon className="w-4 h-4 text-slate-400" /> {label}
    </Link>
  );
}

function PracticeMegaMenu({ onClose }) {
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[920px] max-w-[92vw] bg-white border border-slate-200 rounded-3xl shadow-[0_30px_80px_-30px_rgba(11,45,92,0.30)] p-8 z-50">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {practiceMenu.map((col) => (
          <div key={col.title} data-testid={`megamenu-${col.title.toLowerCase().replace(" ", "-")}`}>
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#F77F00]/30">
              <div className="w-9 h-9 rounded-full bg-[#F77F00] text-white flex items-center justify-center">
                <col.icon className="w-4 h-4" />
              </div>
              <div className="font-display text-sm font-bold tracking-wider text-[#F77F00]">{col.title}</div>
            </div>
            <ul className="space-y-2">
              {col.items.map((it) => (
                <li key={it.to}>
                  <Link onClick={onClose} to={it.to} data-testid={`pm-${col.title}-${it.label}`}
                    className="text-sm text-[#003049] hover:text-[#F77F00] font-medium transition-colors block">
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-7 pt-5 border-t border-slate-100 flex items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[#F77F00]/30 max-w-xs">
            <div className="w-8 h-8 rounded-full bg-[#F77F00] text-white flex items-center justify-center text-xs">📚</div>
            <div className="font-display text-sm font-bold tracking-wider text-[#F77F00]">STUDY TOOLS</div>
          </div>
          <div className="flex gap-5 mt-3 text-sm text-[#003049] font-medium">
            <Link to="/app/library" onClick={onClose} className="hover:text-[#F77F00]">Vocab Bank</Link>
            <Link to="/app/library" onClick={onClose} className="hover:text-[#F77F00]">Grammar</Link>
          </div>
        </div>
        <div className="relative w-72">
          <input type="text" placeholder="Search" className="w-full pr-12 pl-4 py-2.5 rounded-full border border-[#F77F00]/40 text-sm focus:outline-none focus:border-[#F77F00]" />
          <button className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#F77F00] text-white flex items-center justify-center text-sm">🔍</button>
        </div>
      </div>
    </div>
  );
}
